/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { InfraMongoOrderDto } from '../models/infra-mongo-order-dto';
import { InfraOrderDto } from '../models/infra-order-dto';
import { PeripheralConsumptionOrderDto } from '../models/peripheral-consumption-order-dto';
import { SaasOrders } from '../models/saas-orders';


/**
 * Orders management for different kind of services
 */
@Injectable({
  providedIn: 'root',
})
export class OrderManagementService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation createOrdersForInternalCustomers
   */
  static readonly CreateOrdersForInternalCustomersPath = '/api/v1/proposal/{proposalId}/order';

  /**
   * Peripheral Consumption MetaData.
   *
   * This API creates orders for internal customers.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createOrdersForInternalCustomers()` instead.
   *
   * This method doesn't expect any request body.
   */
  createOrdersForInternalCustomers$Response(params: {
    proposalId: string;
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, OrderManagementService.CreateOrdersForInternalCustomersPath, 'post');
    if (params) {
      rb.path('proposalId', params.proposalId, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Peripheral Consumption MetaData.
   *
   * This API creates orders for internal customers.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `createOrdersForInternalCustomers$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  createOrdersForInternalCustomers(params: {
    proposalId: string;
  }): Observable<void> {

    return this.createOrdersForInternalCustomers$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation getAllPerpheralByProfileId
   */
  static readonly GetAllPerpheralByProfileIdPath = '/api/v1/profile/{profileId}/peripheral';

  /**
   * Get Peripheral Consumption Orders.
   *
   * This API gives all the peripheral consumption order details for the specified business profile id.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllPerpheralByProfileId()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllPerpheralByProfileId$Response(params: {
    profileId: string;
    month?: number;
    year?: number;
  }): Observable<StrictHttpResponse<Array<PeripheralConsumptionOrderDto>>> {

    const rb = new RequestBuilder(this.rootUrl, OrderManagementService.GetAllPerpheralByProfileIdPath, 'get');
    if (params) {
      rb.path('profileId', params.profileId, {});
      rb.query('month', params.month, {});
      rb.query('year', params.year, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<PeripheralConsumptionOrderDto>>;
      })
    );
  }

  /**
   * Get Peripheral Consumption Orders.
   *
   * This API gives all the peripheral consumption order details for the specified business profile id.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getAllPerpheralByProfileId$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllPerpheralByProfileId(params: {
    profileId: string;
    month?: number;
    year?: number;
  }): Observable<Array<PeripheralConsumptionOrderDto>> {

    return this.getAllPerpheralByProfileId$Response(params).pipe(
      map((r: StrictHttpResponse<Array<PeripheralConsumptionOrderDto>>) => r.body as Array<PeripheralConsumptionOrderDto>)
    );
  }

  /**
   * Path part for operation savePeripheralConsumptionMetaData
   */
  static readonly SavePeripheralConsumptionMetaDataPath = '/api/v1/profile/{profileId}/peripheral';

  /**
   * Save Peripheral Consumption MetaData.
   *
   * This API saves the peripheral consumption metadata.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `savePeripheralConsumptionMetaData()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  savePeripheralConsumptionMetaData$Response(params: {
    profileId: string;
    body: Array<PeripheralConsumptionOrderDto>
  }): Observable<StrictHttpResponse<Array<PeripheralConsumptionOrderDto>>> {

    const rb = new RequestBuilder(this.rootUrl, OrderManagementService.SavePeripheralConsumptionMetaDataPath, 'post');
    if (params) {
      rb.path('profileId', params.profileId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<PeripheralConsumptionOrderDto>>;
      })
    );
  }

  /**
   * Save Peripheral Consumption MetaData.
   *
   * This API saves the peripheral consumption metadata.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `savePeripheralConsumptionMetaData$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  savePeripheralConsumptionMetaData(params: {
    profileId: string;
    body: Array<PeripheralConsumptionOrderDto>
  }): Observable<Array<PeripheralConsumptionOrderDto>> {

    return this.savePeripheralConsumptionMetaData$Response(params).pipe(
      map((r: StrictHttpResponse<Array<PeripheralConsumptionOrderDto>>) => r.body as Array<PeripheralConsumptionOrderDto>)
    );
  }

  /**
   * Path part for operation getAllSaasOrdersByProfileId
   */
  static readonly GetAllSaasOrdersByProfileIdPath = '/api/v1/saasOrders';

  /**
   * Get SaaS Orders.
   *
   * This API gives all the infra order details for the specified business profile id.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllSaasOrdersByProfileId()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllSaasOrdersByProfileId$Response(params: {
    profileId: string;
  }): Observable<StrictHttpResponse<Array<InfraOrderDto>>> {

    const rb = new RequestBuilder(this.rootUrl, OrderManagementService.GetAllSaasOrdersByProfileIdPath, 'get');
    if (params) {
      rb.query('profileId', params.profileId, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<InfraOrderDto>>;
      })
    );
  }

  /**
   * Get SaaS Orders.
   *
   * This API gives all the infra order details for the specified business profile id.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getAllSaasOrdersByProfileId$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllSaasOrdersByProfileId(params: {
    profileId: string;
  }): Observable<Array<InfraOrderDto>> {

    return this.getAllSaasOrdersByProfileId$Response(params).pipe(
      map((r: StrictHttpResponse<Array<InfraOrderDto>>) => r.body as Array<InfraOrderDto>)
    );
  }

  /**
   * Path part for operation getSaasOrderListByProfileId
   */
  static readonly GetSaasOrderListByProfileIdPath = '/api/v1/profile/{profileId}/saasServices';

  /**
   * Manage Saas Order  List.
   *
   * The API is to fetch the SaaS order list
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getSaasOrderListByProfileId()` instead.
   *
   * This method doesn't expect any request body.
   */
  getSaasOrderListByProfileId$Response(params: {
    profileId: string;
    month?: number;
    year?: number;
  }): Observable<StrictHttpResponse<Array<SaasOrders>>> {

    const rb = new RequestBuilder(this.rootUrl, OrderManagementService.GetSaasOrderListByProfileIdPath, 'get');
    if (params) {
      rb.path('profileId', params.profileId, {});
      rb.query('month', params.month, {});
      rb.query('year', params.year, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<SaasOrders>>;
      })
    );
  }

  /**
   * Manage Saas Order  List.
   *
   * The API is to fetch the SaaS order list
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getSaasOrderListByProfileId$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getSaasOrderListByProfileId(params: {
    profileId: string;
    month?: number;
    year?: number;
  }): Observable<Array<SaasOrders>> {

    return this.getSaasOrderListByProfileId$Response(params).pipe(
      map((r: StrictHttpResponse<Array<SaasOrders>>) => r.body as Array<SaasOrders>)
    );
  }

  /**
   * Path part for operation getAllInfrastructureMongoByProfileId
   */
  static readonly GetAllInfrastructureMongoByProfileIdPath = '/api/v1/mongo/infraOrders';

  /**
   * Infra Mongo Orders.
   *
   * The API is to fetch the infra mongo order list
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllInfrastructureMongoByProfileId()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllInfrastructureMongoByProfileId$Response(params: {
    profileId: string;
  }): Observable<StrictHttpResponse<Array<InfraMongoOrderDto>>> {

    const rb = new RequestBuilder(this.rootUrl, OrderManagementService.GetAllInfrastructureMongoByProfileIdPath, 'get');
    if (params) {
      rb.query('profileId', params.profileId, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<InfraMongoOrderDto>>;
      })
    );
  }

  /**
   * Infra Mongo Orders.
   *
   * The API is to fetch the infra mongo order list
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getAllInfrastructureMongoByProfileId$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllInfrastructureMongoByProfileId(params: {
    profileId: string;
  }): Observable<Array<InfraMongoOrderDto>> {

    return this.getAllInfrastructureMongoByProfileId$Response(params).pipe(
      map((r: StrictHttpResponse<Array<InfraMongoOrderDto>>) => r.body as Array<InfraMongoOrderDto>)
    );
  }

  /**
   * Path part for operation getAllInfrastructureByProfileId
   */
  static readonly GetAllInfrastructureByProfileIdPath = '/api/v1/infraOrders';

  /**
   * Get Infrastrcture Orders.
   *
   * This API gives all the infra order details for the specified business profile id.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllInfrastructureByProfileId()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllInfrastructureByProfileId$Response(params: {
    profileId: string;
  }): Observable<StrictHttpResponse<Array<InfraOrderDto>>> {

    const rb = new RequestBuilder(this.rootUrl, OrderManagementService.GetAllInfrastructureByProfileIdPath, 'get');
    if (params) {
      rb.query('profileId', params.profileId, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<InfraOrderDto>>;
      })
    );
  }

  /**
   * Get Infrastrcture Orders.
   *
   * This API gives all the infra order details for the specified business profile id.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getAllInfrastructureByProfileId$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllInfrastructureByProfileId(params: {
    profileId: string;
  }): Observable<Array<InfraOrderDto>> {

    return this.getAllInfrastructureByProfileId$Response(params).pipe(
      map((r: StrictHttpResponse<Array<InfraOrderDto>>) => r.body as Array<InfraOrderDto>)
    );
  }

}
